import * as React from 'react';
import classNames from 'classnames';

export interface ListProps<TItem = unknown>
    extends React.HTMLAttributes<HTMLOListElement> {
    type?: 'dot' | 'num' | 'check';
    onRenderCell?: (item: TItem) => React.ReactNode;
    /**
     * Slot property for items. Not used if children are given.
     */
    items?: Array<TItem>;
    children?:
        | Array<React.ReactElement<HTMLLIElement>>
        | React.ReactElement<HTMLLIElement>;
}

function InnerList<TItem>(
    props: ListProps<TItem>,
    forwardedRef: React.ForwardRefExoticComponent<HTMLOListElement>,
): JSX.Element {
    const { children, type = 'dot', items, onRenderCell, ...rest } = props;
    // build custom tag for dynamic usage of ordered and unordered lists
    const CustomTag = type === 'num' ? 'ol' : 'ul';

    // construction of class names
    const listClass = classNames('a-list', {
        [`a-list--${type}`]: type,
    });

    const renderChildren =
        children ||
        items.map((item, idx) => (
            <li key={idx}>
                {typeof onRenderCell === 'function' ? onRenderCell(item) : item}
            </li>
        ));

    return (
        <CustomTag className={listClass} ref={forwardedRef} {...rest}>
            {renderChildren}
        </CustomTag>
    );
}

/**
 * @name            a-list
 * @component       atom
 * @description
 * representation of lists
 */

export const List = React.forwardRef(InnerList);
List.displayName = 'List';
